import { getHeaderManifest } from '@carvana/header';
import { MfeConfig } from '@carvana/microfrontend';
import { MfeReact } from '@carvana/microfrontend-react';
import React, { Suspense, useEffect } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation
} from 'react-router-dom';
import styled from 'styled-components';
import AccountLogin from '|/app/account-login';
import ChatUi from '|/app/chat-ui';
import Dashboard from '|/app/dashboard';
import DeveloperControlPanel from '|/app/developer-control-panel';
import GuidedFlow from '|/app/guided-flow';
import Header from '|/app/header';
import '|/ducks/core/reducer';
import '|/ducks/dashboard/reducer';
import '|/ducks/vehicle/reducer';
import { MobileDashboardContextProvider } from '|/app/mobile-dashboard-context';
import { MobileNavigationRailContextProvider } from '|/app/mobile-navigaton-rail-context';
import NextStep from '|/app/next-step';
import ProtectedRoute from '|/app/protect-route';
import Steps from '|/app/steps';
import { SummaryContextProvider } from '|/app/summary-context';
import useDashboardViewModel from '|/app/use-dashboard-vm';
import { UserInitializationProvider } from '|/app/user-initialization-context';
import VehicleRoutes from '|/app/vehicle';
import ErrorBoundary from '|/components/base/error-boundary';
import Loading from '|/components/base/loading';
import { SimpleAppFrameworkContextProvider } from '|/helpers/app-framework';
import { ClickBlockingProvider } from './click-blocking-overlay';
import { DevicePlatformProvider } from './device-platform-context';
import { SpanningLoadTimerProvider } from './spanning-load-timer-context';
import { SupportContactMfeContextProvider } from './support-contact-mfe-context';
import DashboardModal from '../../../library/helpers/src/dashboard-modal/dashboard-modal';
import registry from '../../../library/store/src/registry';
import useSessionTracking from '../helpers/use-session-tracking';
import useTrackClickEvents from '../helpers/use-track-click-events';
import useWebViewRedirect from '../helpers/use-web-view-redirect';

const Page = styled.div`
  width: 100%;
  background-color: var(--colors-neutral-gray-1);
  min-height: 100vh;
`;

const AppContainer = styled.div`
  max-width: 1800px;
  margin: 0 auto;
  min-height: 100vh;
  box-shadow: 1px 2px 3px 1px rgba(24 53 88 / 10%);
`;

const StepRedirect = () => {
  const { pathname } = useLocation();
  return pathname.startsWith('/dashboard') ? (
    <Navigate to="/dashboard" replace={true} />
  ) : (
    <Navigate to={`/steps${pathname}`} replace={true} />
  );
};

const App = () => {
  useWebViewRedirect();
  useEffect(() => {
    MfeConfig({
      name: 'purchase-ui',
      ENV: process.env.LOCAL === 'true' ? 'local' : process.env.NODE_ENV,
      debug: process.env.NODE_ENV !== 'production',
      Render: MfeReact,
      globals: {
        React: () => import('react'),
        ReactDOM: () => import('react-dom'),
        styled: () => import('styled-components'),
        'styled-components': () => import('styled-components'),
        PropTypes: () => import('prop-types'),
        'prop-types': () => import('prop-types')
      },
      files: [
        [
          'HeaderModule',
          [
            {
              component: 'default',
              default: {
                file: 'index.js',
                manifest: getHeaderManifest(process.env.NODE_ENV)
              }
            }
          ]
        ],
        [
          'CoreSupportDropdown',
          [
            {
              component: 'default',
              default: {
                file: 'index.js',
                manifest: `${process.env.SUPPORT_AND_CONTACT_BASE_URL}/support-dropdown-module/assets-manifest.json`
              }
            }
          ]
        ]
      ]
    });
  }, []);

  useTrackClickEvents();
  useSessionTracking();
  const {
    dashboard,
    orderSummary,
    ReloadDashboard,
    UpdateDashboardAndSummary
  } = useDashboardViewModel();
  const navigate = useNavigate();

  return (
    <SimpleAppFrameworkContextProvider>
      <UserInitializationProvider>
        <DevicePlatformProvider platform="web">
          <SpanningLoadTimerProvider>
            <Page>
              <AppContainer>
                <SummaryContextProvider>
                  <MobileDashboardContextProvider>
                    <MobileNavigationRailContextProvider>
                      <SupportContactMfeContextProvider>
                        <Header orderSummary={orderSummary} />
                        <ChatUi />
                        <ClickBlockingProvider>
                          <Suspense fallback={<Loading />}>
                            <ErrorBoundary>
                              <SimpleAppFrameworkContextProvider>
                                <Routes>
                                  <Route path="/">
                                    <Route
                                      path="/"
                                      element={
                                        <ProtectedRoute>
                                          <GuidedFlow />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="dashboard"
                                      element={
                                        <ProtectedRoute>
                                          <Dashboard
                                            dashboard={dashboard}
                                            summary={orderSummary}
                                          />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="next-step"
                                      element={
                                        <ProtectedRoute>
                                          <NextStep
                                            UpdateDashboardAndSummary={
                                              UpdateDashboardAndSummary
                                            }
                                          />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="vehicle/*"
                                      element={
                                        <ProtectedRoute>
                                          <VehicleRoutes />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="steps/*"
                                      element={
                                        <ProtectedRoute>
                                          <Steps
                                            dashboard={dashboard}
                                            summary={orderSummary}
                                            ReloadDashboard={ReloadDashboard}
                                            GetDashboardAndRouteToNextStep={() => {
                                              navigate('/next-step');
                                            }}
                                          />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="account"
                                      element={<AccountLogin />}
                                    />
                                    <Route
                                      path="*"
                                      element={
                                        <ProtectedRoute>
                                          <StepRedirect />
                                        </ProtectedRoute>
                                      }
                                    />
                                  </Route>
                                </Routes>
                                <DeveloperControlPanel />
                              </SimpleAppFrameworkContextProvider>
                            </ErrorBoundary>
                          </Suspense>
                        </ClickBlockingProvider>
                      </SupportContactMfeContextProvider>
                      <DashboardModal registry={registry} />
                    </MobileNavigationRailContextProvider>
                  </MobileDashboardContextProvider>
                </SummaryContextProvider>
              </AppContainer>
            </Page>
          </SpanningLoadTimerProvider>
        </DevicePlatformProvider>
      </UserInitializationProvider>
    </SimpleAppFrameworkContextProvider>
  );
};

export default App;
